import { useEffect, useState } from "react";

/**
 * "Mobile Code" modal — shows the community's membership code (the token in
 * /membership/{code}/login) so a member can type it into the mobile app.
 * Read-only, with a copy button.
 */
export default function MobileCodeModal({ code, primary = "#0274F7", onClose }) {
  const [copied, setCopied] = useState(false);

  // Reset the "Copied" confirmation after a moment so the button returns to its
  // idle label instead of staying stuck on success.
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // navigator.clipboard is unavailable on insecure origins (plain http, which
      // the local vhosts use) — fall back to the legacy execCommand path.
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
      } catch {
        /* leave the code on screen for manual selection */
      }
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-surface shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4" style={{ background: `${primary}0d` }}>
          <h2 className="text-base font-bold text-gray-900">Mobile Code</h2>
          <button onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <div className="p-5">
          {/* Phone showing a filled code field — says "type this code into the
              app" without needing a caption. Decorative, so hidden from AT. */}
          <div className="flex justify-center pb-1 pt-1">
            <svg viewBox="8 4 104 88" className="h-28 w-auto" fill="none" aria-hidden="true" focusable="false">
              {/* glow behind the phone */}
              <circle cx="60" cy="48" r="34" fill={primary} opacity="0.07" />

              {/* handset */}
              <rect x="42" y="10" width="36" height="76" rx="7" fill="#fff" stroke={primary} strokeWidth="2.5" />
              <path d="M55 16h10" stroke={primary} strokeWidth="2" strokeLinecap="round" opacity="0.45" />

              {/* screen: code entry with the last box still empty */}
              <rect x="47" y="23" width="26" height="14" rx="3" fill={primary} opacity="0.1" />
              <path d="M51 30h6M51 34h12" stroke={primary} strokeWidth="1.6" strokeLinecap="round" opacity="0.5" />
              <rect x="47.5" y="44.5" width="8" height="11" rx="2" fill={primary} opacity="0.16" />
              <rect x="58.5" y="44.5" width="8" height="11" rx="2" fill={primary} opacity="0.16" />
              <rect x="69.5" y="44.5" width="3.5" height="11" rx="1.75" stroke={primary} strokeWidth="1.4" strokeDasharray="2.5 2.5" opacity="0.6" />

              {/* unlock confirmation */}
              <circle cx="60" cy="70" r="8" fill={primary} opacity="0.12" />
              <path d="M56.5 70l2.5 2.5 4.5-5" stroke={primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

              {/* the code travelling from clipboard to phone */}
              <rect x="12" y="34" width="18" height="22" rx="3.5" fill="#fff" stroke={primary} strokeWidth="1.8" opacity="0.75" />
              <path d="M17 33.5h8" stroke={primary} strokeWidth="1.8" strokeLinecap="round" opacity="0.75" />
              <path d="M16.5 42h9M16.5 46.5h6" stroke={primary} strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
              <path d="M33 45h7" stroke={primary} strokeWidth="1.8" strokeLinecap="round" strokeDasharray="3 3" opacity="0.55" />

              {/* signal ticks — the phone is connecting */}
              <path d="M88 44a12 12 0 010 8M94 40a20 20 0 010 16" stroke={primary} strokeWidth="1.8" strokeLinecap="round" opacity="0.35" />
            </svg>
          </div>

          <p className="text-center text-sm text-gray-500">
            Use this membership code to sign in on the mobile app.
          </p>

          <div className="mt-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50/60 p-3">
            <code className="min-w-0 flex-1 select-all break-all font-mono text-lg font-bold tracking-wide text-gray-900">
              {code}
            </code>
            <button
              onClick={copy}
              aria-label="Copy membership code"
              className="flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white transition hover:opacity-90 active:scale-95"
              style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
            >
              {copied ? (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <rect x="9" y="9" width="11" height="11" rx="2" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a1 1 0 01-1-1V4a1 1 0 011-1h10a1 1 0 011 1v1" />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
